import { useCallback, useEffect, useState, type FormEvent } from "react";
import AdminGuard from "../components/AdminGuard";
import AdminLayout from "../components/AdminLayout";
import { useAdminSession } from "../components/AdminSessionContext";
import { ROLE_META, formatDateTime, type StaffMember, type StaffRoleKey } from "../adminConfig";
import { callStaffApi } from "@/lib/staffApi";

interface StaffListResponse {
  staff: StaffMember[];
}

interface ToastState {
  type: "ok" | "err";
  text: string;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-300 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

function roleBadge(role: StaffRoleKey) {
  const meta = ROLE_META[role];
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.badge}`}
    >
      <i className={`${meta.icon} text-sm`}></i>
      {meta.label}
    </span>
  );
}

function StaffContent() {
  const { session, role: myRole } = useAdminSession();
  const myId = session?.user?.id || "";

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<ToastState | null>(null);
  const [busyId, setBusyId] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
    role: "sales" as StaffRoleKey,
  });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [editTarget, setEditTarget] = useState<StaffMember | null>(null);
  const [editUsername, setEditUsername] = useState("");
  const [editError, setEditError] = useState("");
  const [editing, setEditing] = useState(false);

  const [resetTarget, setResetTarget] = useState<StaffMember | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetting, setResetting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<StaffMember | null>(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = (type: "ok" | "err", text: string) => {
    setToast({ type, text });
    window.setTimeout(() => setToast(null), 2600);
  };

  const loadStaff = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await callStaffApi<StaffListResponse>({ action: "list" });
    if (res.ok && res.data) {
      setStaff(res.data.staff || []);
    } else {
      setError(res.error || "员工列表加载失败");
      setStaff([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadStaff();
  }, [loadStaff]);

  const closeAdd = () => {
    setAddOpen(false);
    setForm({ username: "", password: "", role: "sales" });
    setFormError("");
  };

  const handleAddSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.username.trim() || !form.password) {
      setFormError("请填写用户名和初始密码");
      return;
    }
    if (form.password.length < 6) {
      setFormError("初始密码至少 6 位");
      return;
    }
    setSaving(true);
    setFormError("");
    const res = await callStaffApi({
      action: "create",
      username: form.username.trim().toLowerCase(),
      password: form.password,
      role: form.role,
    });
    setSaving(false);
    if (res.ok) {
      closeAdd();
      showToast("ok", "员工账号已创建");
      void loadStaff();
    } else {
      setFormError(res.error || "创建失败，请稍后重试");
    }
  };

  const handleEditSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editTarget) return;
    const next = editUsername.trim().toLowerCase();
    if (!next || next === (editTarget.username || "")) {
      setEditTarget(null);
      return;
    }
    setEditing(true);
    setEditError("");
    const res = await callStaffApi({
      action: "update",
      userId: editTarget.user_id,
      username: next,
    });
    setEditing(false);
    if (res.ok) {
      setEditTarget(null);
      showToast("ok", "用户名已更新");
      void loadStaff();
    } else {
      setEditError(res.error || "更新失败，请稍后重试");
    }
  };

  const handleRoleChange = async (member: StaffMember, nextRole: StaffRoleKey) => {
    if (nextRole === member.role) return;
    setBusyId(member.user_id);
    const res = await callStaffApi({ action: "update", userId: member.user_id, role: nextRole });
    setBusyId("");
    if (res.ok) {
      showToast("ok", "角色已更新");
      void loadStaff();
    } else {
      showToast("err", res.error || "角色更新失败");
    }
  };

  const handleResetSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!resetTarget) return;
    if (newPassword.length < 6) {
      setResetError("新密码至少 6 位");
      return;
    }
    setResetting(true);
    setResetError("");
    const res = await callStaffApi({
      action: "reset_password",
      userId: resetTarget.user_id,
      password: newPassword,
    });
    setResetting(false);
    if (res.ok) {
      setResetTarget(null);
      setNewPassword("");
      showToast("ok", "密码已重置");
    } else {
      setResetError(res.error || "重置失败，请稍后重试");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const res = await callStaffApi({ action: "delete", userId: deleteTarget.user_id });
    setDeleting(false);
    if (res.ok) {
      setDeleteTarget(null);
      showToast("ok", "员工账号已删除");
      void loadStaff();
    } else {
      setDeleteTarget(null);
      showToast("err", res.error || "删除失败");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {toast && (
        <div
          className={`fixed left-1/2 top-5 z-[60] -translate-x-1/2 rounded-md px-4 py-2.5 text-sm font-medium text-background-50 ${
            toast.type === "ok" ? "bg-primary-600" : "bg-accent-600"
          }`}
        >
          {toast.text}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            员工管理
          </h1>
          <p className="mt-1 text-sm text-foreground-500">
            共 {staff.length} 名内部员工，可分配管理员或销售角色
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-user-add-line"></i>
          添加员工
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {(Object.keys(ROLE_META) as StaffRoleKey[]).map((key) => {
          const meta = ROLE_META[key];
          const active = myRole === key;
          return (
            <div
              key={key}
              className="rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <div className="flex items-center justify-between gap-3">
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-lg ${meta.badge}`}
                >
                  <i className={`${meta.icon} text-lg`}></i>
                </span>
                {active && (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-500">
                    <i className="ri-checkbox-circle-line"></i>
                    当前角色
                  </span>
                )}
              </div>
              <h3 className="mt-3 font-heading text-sm font-semibold text-foreground-900">
                {meta.label}
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-foreground-500">{meta.desc}</p>
            </div>
          );
        })}
      </div>

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-left text-xs font-medium text-foreground-500">
                <th className="px-4 py-3 font-medium">员工</th>
                <th className="px-4 py-3 font-medium">角色</th>
                <th className="px-4 py-3 font-medium">加入时间</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 3 }).map((_, index) => (
                  <tr key={`staff-skeleton-${index}`} className="border-b border-background-100">
                    <td className="px-4 py-3.5">
                      <span className="block h-3.5 w-40 animate-pulse rounded bg-background-200"></span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="block h-3.5 w-20 animate-pulse rounded bg-background-200"></span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="block h-3.5 w-28 animate-pulse rounded bg-background-200"></span>
                    </td>
                    <td className="px-4 py-3.5"></td>
                  </tr>
                ))}

              {!loading &&
                staff.map((member) => {
                  const isMe = member.user_id === myId;
                  return (
                    <tr
                      key={member.user_id}
                      className="border-b border-background-100 transition-colors last:border-b-0 hover:bg-background-100/70"
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
                            {(member.username || "员").slice(0, 1)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground-900">
                              {member.username || "未命名员工"}
                              {isMe && (
                                <span className="ml-2 rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-500">
                                  我
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <select
                          value={member.role}
                          disabled={busyId === member.user_id}
                          onChange={(e) =>
                            void handleRoleChange(member, e.target.value as StaffRoleKey)
                          }
                          className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 disabled:opacity-60"
                        >
                          <option value="admin">管理员</option>
                          <option value="sales">销售</option>
                        </select>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-xs text-foreground-500">
                        {formatDateTime(member.created_at)}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditTarget(member);
                              setEditUsername(member.username || "");
                              setEditError("");
                            }}
                            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                          >
                            <i className="ri-edit-2-line"></i>
                            改用户名
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setResetTarget(member);
                              setNewPassword("");
                              setResetError("");
                            }}
                            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                          >
                            <i className="ri-key-2-line"></i>
                            重置密码
                          </button>
                          <button
                            type="button"
                            disabled={isMe}
                            onClick={() => setDeleteTarget(member)}
                            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <i className="ri-delete-bin-line"></i>
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {error && (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className="ri-error-warning-line text-xl"></i>
            </span>
            <p className="text-sm text-foreground-700">{error}</p>
            <button
              type="button"
              onClick={() => void loadStaff()}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重新加载
            </button>
          </div>
        )}

        {!loading && !error && staff.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-team-line text-xl"></i>
            </span>
            <p className="text-sm text-foreground-600">还没有其他员工，先添加一位吧</p>
          </div>
        )}
      </div>

      {addOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={closeAdd}></div>
          <div className="relative flex h-full w-full max-w-md flex-col border-l border-background-200 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <div>
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  添加员工账号
                </h2>
                <p className="mt-0.5 text-xs text-foreground-400">
                  创建后员工即可用该用户名与密码登录系统
                </p>
              </div>
              <button
                type="button"
                onClick={closeAdd}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">
                  用户名 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.username}
                  placeholder="如：wangmeili（3-30 位小写字母/数字）"
                  onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-foreground-400">
                  员工用该用户名 + 密码登录系统，创建后可随时修改。
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">
                  初始密码 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.password}
                  placeholder="至少 6 位，可让员工登录后自行修改"
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  className={inputClass}
                />
              </div>

              <div>
                <p className="mb-2 block text-sm font-medium text-foreground-800">角色权限</p>
                <div className="flex flex-col gap-2">
                  {(Object.keys(ROLE_META) as StaffRoleKey[]).map((key) => {
                    const meta = ROLE_META[key];
                    const active = form.role === key;
                    return (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setForm((prev) => ({ ...prev, role: key }))}
                        className={`flex cursor-pointer items-start gap-3 rounded-md border px-3.5 py-3 text-left transition-colors ${
                          active
                            ? "border-primary-400 bg-primary-50"
                            : "border-background-300 bg-background-50 hover:bg-background-100"
                        }`}
                      >
                        <span className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-md ${meta.badge}`}>
                          <i className={`${meta.icon} text-base`}></i>
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-foreground-900">
                            {meta.label}
                          </span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-foreground-500">
                            {meta.desc}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {formError && (
                <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5 text-xs text-accent-800">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <span>{formError}</span>
                </div>
              )}

              <div className="mt-auto flex flex-col gap-2 sm:flex-row">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-user-add-line"></i>}
                  创建账号
                </button>
                <button
                  type="button"
                  onClick={closeAdd}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
                >
                  取消
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setResetTarget(null)}
          ></div>
          <form
            onSubmit={handleResetSubmit}
            className="relative w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6"
          >
            <h2 className="font-heading text-base font-semibold text-foreground-950">重置登录密码</h2>
            <p className="mt-1 text-xs text-foreground-500">
              为 {resetTarget.username || "该员工"} 设置新的登录密码
            </p>
            <input
              type="text"
              value={newPassword}
              placeholder="请输入新密码（至少 6 位）"
              onChange={(e) => setNewPassword(e.target.value)}
              className={`mt-4 ${inputClass}`}
            />
            {resetError && <p className="mt-2 text-xs text-accent-600">{resetError}</p>}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setResetTarget(null)}
                className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={resetting}
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resetting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-key-2-line"></i>}
                确认重置
              </button>
            </div>
          </form>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setDeleteTarget(null)}
          ></div>
          <div className="relative w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className="ri-delete-bin-line text-xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">
              删除员工账号
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground-500">
              即将删除 {deleteTarget.username || "该员工"} 的账号，删除后该员工将无法登录系统。此操作不可撤销。
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-delete-bin-line"></i>}
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setEditTarget(null)}
          ></div>
          <form
            onSubmit={handleEditSubmit}
            className="relative w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6"
          >
            <h2 className="font-heading text-base font-semibold text-foreground-950">
              修改用户名
            </h2>
            <p className="mt-1 text-xs text-foreground-500">
              当前用户名：{editTarget.username || "—"}，修改后该员工需用新用户名登录
            </p>
            <input
              type="text"
              value={editUsername}
              placeholder="请输入新的用户名"
              onChange={(e) => setEditUsername(e.target.value)}
              className={`mt-4 ${inputClass}`}
            />
            {editError && <p className="mt-2 text-xs text-accent-600">{editError}</p>}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={editing}
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {editing ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                保存修改
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3">
        <i className="ri-shield-check-line mt-0.5 text-secondary-600"></i>
        <p className="text-xs leading-relaxed text-secondary-900">
          {roleBadge(myRole || "sales")}
          <span className="ml-2">
            销售角色仅可查看会员资料，手机号会自动脱敏；管理员可查看完整资料、导入数据与管理员工。
          </span>
        </p>
      </div>
    </div>
  );
}

export default function AdminStaff() {
  return (
    <AdminGuard requireAdmin>
      <AdminLayout>
        <StaffContent />
      </AdminLayout>
    </AdminGuard>
  );
}